import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError,
  approveCorrection,
  cancelCorrection,
  fetchCorrections,
  fetchMembers,
  rejectCorrection,
} from '../api/client';
import type { AttendanceCorrection } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { buildComparison, decisionAbility } from '../lib/correction-decision';
import { formatCorrectionType } from '../lib/correction-form';
import { formatDateInZone, formatTimeInZone } from '../lib/format';
import { pengajuKoreksi, petaNamaAnggota, type PengajuKoreksi } from '../lib/pengaju-koreksi';
import { EmptyState, ErrorCard, LoadingState } from '../states';

type Filter = 'PENDING' | 'APPROVED' | 'REJECTED';

/**
 * Antrean koreksi.
 *
 * Scope-nya milik SERVER: karyawan melihat pengajuannya sendiri, manajer melihat timnya.
 * Halaman ini tidak mengirim filter pengguna apa pun, jadi membuka URL-nya tanpa wewenang
 * menghasilkan daftar kosong atau `403` — bukan data orang lain.
 */
export function CorrectionQueuePage(): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const [filter, setFilter] = useState<Filter>('PENDING');
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { state, reload } = useAsync(
    () => fetchCorrections(session.auth, filter),
    [session.auth, filter],
  );

  /*
   * Nama diambil TERPISAH dari antreannya, dan kegagalannya sengaja tidak mengosongkan layar.
   *
   * Respons koreksi hanya membawa UUID; nama hidup di `GET /api/members`. Menggabungkan keduanya
   * menjadi satu permintaan berarti satu daftar anggota yang gagal memuat akan menghapus seluruh
   * antrean — menukar "kartu tanpa nama" dengan "tidak ada kartu sama sekali", yang jelas lebih
   * buruk bagi manajer yang sedang menunggu memutuskan.
   */
  const { state: anggota } = useAsync(() => fetchMembers(session.auth), [session.auth]);
  const petaNama = useMemo(
    () => petaNamaAnggota(anggota.status === 'ready' ? anggota.data : []),
    [anggota],
  );

  async function run(id: string, action: () => Promise<unknown>): Promise<void> {
    setBusyId(id);
    setActionError(null);
    try {
      await action();
      reload();
    } catch (caught: unknown) {
      setActionError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <section className="card">
        <div className="row space-between">
          <h2>Koreksi absensi</h2>
          <Link to="/attendance/corrections/new">Ajukan</Link>
        </div>
        <div className="row" role="group" aria-label="Saring status">
          {(['PENDING', 'APPROVED', 'REJECTED'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={filter === value ? 'chip active' : 'chip'}
              onClick={() => setFilter(value)}
            >
              {value === 'PENDING' ? 'Menunggu' : value === 'APPROVED' ? 'Disetujui' : 'Ditolak'}
            </button>
          ))}
        </div>
      </section>

      {actionError ? <ErrorCard error={actionError} /> : null}
      {state.status === 'loading' ? <LoadingState label="Memuat antrean…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready' && state.data.length === 0 ? (
        <EmptyState
          title={
            filter === 'PENDING' ? 'Tidak ada yang menunggu keputusan' : 'Belum ada di daftar ini'
          }
          hint={
            filter === 'PENDING'
              ? 'Pengajuan koreksi yang perlu diputuskan akan muncul di sini.'
              : 'Ubah saringan status untuk melihat pengajuan lain.'
          }
        />
      ) : null}

      {state.status === 'ready' && state.data.length > 0 ? (
        <ul className="list">
          {state.data.map((correction) => (
            <CorrectionCard
              key={correction.id}
              correction={correction}
              pengaju={pengajuKoreksi(petaNama, correction)}
              timezone={timezone}
              actorUserId={session.me.userId}
              permissions={session.me.permissions}
              busy={busyId === correction.id}
              onApprove={(note) =>
                run(correction.id, () =>
                  approveCorrection(session.auth, correction.id, { reviewNote: note || null }),
                )
              }
              onReject={(note) =>
                run(correction.id, () =>
                  rejectCorrection(session.auth, correction.id, { reviewNote: note || null }),
                )
              }
              onCancel={() =>
                run(correction.id, () => cancelCorrection(session.auth, correction.id))
              }
            />
          ))}
        </ul>
      ) : null}
    </>
  );
}

function CorrectionCard({
  correction,
  pengaju,
  timezone,
  actorUserId,
  permissions,
  busy,
  onApprove,
  onReject,
  onCancel,
}: {
  readonly correction: AttendanceCorrection;
  readonly pengaju: PengajuKoreksi;
  readonly timezone: string;
  readonly actorUserId: string;
  readonly permissions: readonly string[];
  readonly busy: boolean;
  readonly onApprove: (note: string) => void;
  readonly onReject: (note: string) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const [note, setNote] = useState('');
  const ability = decisionAbility({ userId: actorUserId, permissions }, correction);
  const rows = buildComparison(correction.requestedPayload, {});

  return (
    <li className="card list-item">
      <div className="row space-between">
        <strong>{formatCorrectionType(correction.correctionType)}</strong>
        <span className={`badge status-${correction.status}`}>{correction.status}</span>
      </div>

      {/*
        NAMA lebih dulu daripada tanggal dan alasan. Yang pertama ditanyakan manajer sebelum
        menekan Setujui adalah "ini siapa?" — dan sebelumnya kartu ini tidak pernah menjawabnya.
      */}
      <p className="pengaju">
        <strong>{pengaju.subjek}</strong>
        {pengaju.diajukanOleh ? (
          <span className="muted small"> · diajukan oleh {pengaju.diajukanOleh}</span>
        ) : null}
      </p>

      <p className="muted small">
        Hari kerja {formatDateInZone(`${correction.workDate}T00:00:00.000Z`, 'UTC')} · diajukan{' '}
        {formatTimeInZone(correction.createdAt, timezone)}
      </p>

      <p>{correction.reason}</p>

      {rows.length > 0 ? (
        <table className="compare">
          <thead>
            <tr>
              <th scope="col">Hal</th>
              <th scope="col">Sebelum</th>
              <th scope="col">Sesudah</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                <td>{row.before}</td>
                <td>
                  <strong>{row.after}</strong>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {correction.reviewNote ? (
        <p className="muted small">Catatan penilai: {correction.reviewNote}</p>
      ) : null}

      {/* Alasan terhalang DIKATAKAN, bukan tombol yang hilang diam-diam — ATT-009 yang muncul
          setelah menekan tombol terasa seperti sistem rusak, padahal aturannya benar. */}
      {ability.blockedReason ? <p className="muted small">{ability.blockedReason}</p> : null}

      {ability.canApprove || ability.canReject ? (
        <>
          <label htmlFor={`note-${correction.id}`}>Catatan keputusan (opsional)</label>
          <input
            id={`note-${correction.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </>
      ) : null}

      <div className="row">
        {ability.canApprove ? (
          <button
            type="button"
            className="button-primary"
            disabled={busy}
            onClick={() => onApprove(note)}
          >
            Setujui
          </button>
        ) : null}
        {ability.canReject ? (
          <button type="button" disabled={busy} onClick={() => onReject(note)}>
            Tolak
          </button>
        ) : null}
        {ability.canCancel ? (
          <button type="button" disabled={busy} onClick={onCancel}>
            Batalkan pengajuan saya
          </button>
        ) : null}
      </div>
    </li>
  );
}
